/* ============================================================
   calloutLocator.js — best-effort "zoom to the part's callout"

   Given an assembly diagram image URL and a part description that
   carries a callout code in parentheses (e.g. "...SEGMENT (L8S-18-R)"),
   find where that callout label (e.g. "8S") sits ON the diagram image
   and return its center as a {xFrac, yFrac} fraction (0..1) so the
   caller can aim a CSS transform-origin + zoom at it.

   How: the diagrams are flat raster scans, so there's no stored
   label->coordinate map. We OCR the image in the browser with
   Tesseract.js (lazy-loaded from CDN — no bundle/webpack impact) and
   match the part's callout token against the recognized words.

   This is BEST-EFFORT by design:
     - OCR must be able to READ the image pixels. The diagram lives on
       Azure Blob storage; if that storage isn't configured to allow
       cross-origin reads (CORS), the browser blocks the fetch and OCR
       can't run. In that case (and on any other failure) every function
       here resolves to null and the caller simply leaves the diagram
       docked without zooming.
     - OCR accuracy on busy engineering drawings is imperfect; a missed
       or misread label also resolves to null (dock, no zoom).

   Results are cached per image URL so the (slow) OCR pass runs at most
   once per diagram for the life of the page.
   ============================================================ */

// jsDelivr UMD build — exposes window.Tesseract. v2 reliably returns
// data.words with pixel bounding boxes, which is what we need.
var TESSERACT_CDN =
  "https://cdn.jsdelivr.net/npm/tesseract.js@2.1.5/dist/tesseract.min.js";

// ===== GATE: client-side OCR requires CORS on the image storage =====
// Reading a diagram's PIXELS (what OCR needs) requires the Azure Blob
// container (steeleimages...) to send Access-Control-Allow-Origin for the
// app origin. It currently does NOT, so the browser blocks the read and
// throws an UNCAUGHT "Tainted canvases may not be exported" SecurityError
// from inside Tesseract — which also disrupts the dock's image-switching.
// Keep this false so locateCallout() no-ops cleanly: the diagram still
// docks (no console errors, switching works), there's just no auto-zoom.
// To restore the callout zoom: enable CORS on the storage account
// (GET from the app origin), then flip this to true and rebuild.
var CALLOUT_ZOOM_ENABLED = false;

var _tesseractPromise = null;
var _ocrCache = {}; // imageUrl -> Promise<{words, width, height}>

function loadTesseract() {
  if (typeof window !== "undefined" && window.Tesseract) {
    return Promise.resolve(window.Tesseract);
  }
  if (_tesseractPromise) return _tesseractPromise;
  _tesseractPromise = new Promise(function (resolve, reject) {
    var s = document.createElement("script");
    s.src = TESSERACT_CDN;
    s.async = true;
    s.onload = function () {
      if (window.Tesseract) resolve(window.Tesseract);
      else reject(new Error("Tesseract failed to initialize after load"));
    };
    s.onerror = function () {
      _tesseractPromise = null; // allow a later retry
      reject(new Error("Tesseract CDN script failed to load"));
    };
    document.head.appendChild(s);
  });
  return _tesseractPromise;
}

// Natural pixel dimensions of the image — OCR bounding boxes are in this
// coordinate space, so we need it to convert to fractions.
function imageDimensions(url) {
  return new Promise(function (resolve) {
    var im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = function () {
      resolve({ w: im.naturalWidth || 0, h: im.naturalHeight || 0 });
    };
    im.onerror = function () {
      resolve(null);
    };
    im.src = url;
  });
}

function ocrImage(url) {
  if (_ocrCache[url]) return _ocrCache[url];
  _ocrCache[url] = (function () {
    return loadTesseract().then(function (T) {
      return Promise.all([
        T.recognize(url, "eng", { logger: function () {} }),
        imageDimensions(url),
      ]).then(function (pair) {
        var data = (pair[0] && pair[0].data) || {};
        var dims = pair[1] || {};
        var words = (data.words || []).map(function (w) {
          return { text: w.text, bbox: w.bbox, conf: w.confidence };
        });
        var width = data.imageWidth || dims.w || 0;
        var height = data.imageHeight || dims.h || 0;
        return { words: words, width: width, height: height };
      });
    });
  })().catch(function (e) {
    // Drop the cached rejection so a later click can retry, then rethrow
    // for the caller's .catch (which downgrades to "dock, no zoom").
    _ocrCache[url] = null;
    throw e;
  });
  return _ocrCache[url];
}

// Pull likely callout tokens out of the FIRST (...) group in a part
// description. e.g. "SPIRAL GROOVE LINER SEGMENT (L8S-18-R)" -> ["L8S","8S","18"]
// ordered most-specific-first (number+letter codes like "8S" before bare
// numbers like "18").
export function calloutCandidates(description) {
  var m = /\(([^)]+)\)/.exec(description || "");
  if (!m) return [];
  var content = m[1];
  var cands = [];
  var seg0 = content.split(/[-\s_/.,]/)[0];
  if (seg0) {
    cands.push(seg0);
    var stripped = seg0.replace(/^[A-Za-z]+/, ""); // "L8S" -> "8S"
    if (stripped && stripped !== seg0) cands.push(stripped);
  }
  var re = /\d+[A-Za-z]{0,3}/g;
  var mm;
  while ((mm = re.exec(content))) cands.push(mm[0]);

  var seen = {};
  var out = [];
  cands.forEach(function (c) {
    var u = String(c).toUpperCase();
    if (u && !seen[u]) {
      seen[u] = 1;
      out.push(u);
    }
  });
  // Prefer tokens that end in a letter (real balloon labels like "8S"),
  // then longer tokens.
  out.sort(function (a, b) {
    var la = /[A-Za-z]$/.test(a) ? 0 : 1;
    var lb = /[A-Za-z]$/.test(b) ? 0 : 1;
    if (la !== lb) return la - lb;
    return b.length - a.length;
  });
  return out;
}

function normalize(s) {
  return String(s || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
}

/**
 * Locate a part's callout on a diagram image.
 * @returns Promise<{xFrac:number,yFrac:number,matched:string}|null>
 *          null on no-parenthetical / no-OCR / no-match / any failure.
 */
export function locateCallout(imageUrl, description) {
  // Disabled until the image storage sends CORS headers — see the
  // CALLOUT_ZOOM_ENABLED note above. No-op keeps the dock clean + error-free.
  if (!CALLOUT_ZOOM_ENABLED) return Promise.resolve(null);
  if (!imageUrl) return Promise.resolve(null);
  var candidates = calloutCandidates(description);
  if (!candidates.length) return Promise.resolve(null);

  return ocrImage(imageUrl)
    .then(function (ocr) {
      if (!ocr || !ocr.width || !ocr.height || !ocr.words.length) return null;
      for (var i = 0; i < candidates.length; i++) {
        var want = normalize(candidates[i]);
        if (!want) continue;
        var best = null;
        for (var j = 0; j < ocr.words.length; j++) {
          var w = ocr.words[j];
          if (normalize(w.text) === want && w.bbox) {
            if (!best || (w.conf || 0) > (best.conf || 0)) best = w;
          }
        }
        if (best) {
          var cx = (best.bbox.x0 + best.bbox.x1) / 2 / ocr.width;
          var cy = (best.bbox.y0 + best.bbox.y1) / 2 / ocr.height;
          return {
            xFrac: Math.min(Math.max(cx, 0), 1),
            yFrac: Math.min(Math.max(cy, 0), 1),
            matched: candidates[i],
          };
        }
      }
      return null;
    })
    .catch(function () {
      return null; // best-effort: any failure => dock without zoom
    });
}

/* ============================================================
   PRE-COMPUTED callout lookup (Azure batch OCR -> assembly-callouts.json)

   The live in-browser OCR above is CORS-blocked + low-accuracy, so callout
   zoom is instead driven by a map built offline with Azure AI Vision:
     imagePath -> { w, h, t:[ {s, x, y, w, h, c} ] }   (native-pixel boxes)

   findCalloutBox() matches a part's Item value against one image's entry and
   returns the balloon center as {xFrac,yFrac} (+ box) for the SAME
   transform-origin/zoom the dock already uses. Pure + synchronous.
   ============================================================ */
export function findCalloutBox(entry, item) {
  if (!entry || !entry.t || !entry.t.length || !entry.w || !entry.h) return null;
  var want = normalize(item);
  if (!want) return null;
  var toks = entry.t;

  // 1) exact single-token match — covers "2A", "145F", and "70AP" when Azure
  //    read the two balloon lines as one token. Highest confidence wins.
  var exact = null;
  for (var i = 0; i < toks.length; i++) {
    if (normalize(toks[i].s) === want && (!exact || (toks[i].c || 0) > (exact.c || 0))) {
      exact = toks[i];
    }
  }
  if (exact) return _frac(exact, entry);

  // 2) two-line balloon: a digit-prefix over an alpha/num suffix ("70" over
  //    "AP" => "70AP"). Find a prefix token with a suffix token stacked just
  //    beneath it (x-aligned) and union their boxes.
  var m = /^(\d+)([A-Z0-9]+)$/.exec(want);
  if (m) {
    var pre = m[1], suf = m[2], best = null;
    for (var a = 0; a < toks.length; a++) {
      if (normalize(toks[a].s) !== pre) continue;
      for (var b = 0; b < toks.length; b++) {
        if (normalize(toks[b].s) !== suf) continue;
        var P = toks[a], S = toks[b];
        var dx = Math.abs((P.x + P.w / 2) - (S.x + S.w / 2));
        var gap = S.y - (P.y + P.h); // suffix sits just below prefix
        if (dx <= Math.max(P.w, S.w) && gap >= -P.h && gap <= P.h * 1.8) {
          var score = (P.c || 0) + (S.c || 0) - dx;
          if (!best || score > best.score) {
            var x = Math.min(P.x, S.x), y = Math.min(P.y, S.y);
            best = { score: score, box: { x: x, y: y, w: Math.max(P.x + P.w, S.x + S.w) - x, h: Math.max(P.y + P.h, S.y + S.h) - y } };
          }
        }
      }
    }
    if (best) return _frac(best.box, entry);
  }
  return null;
}

function _frac(box, entry) {
  return {
    xFrac: Math.min(Math.max((box.x + box.w / 2) / entry.w, 0), 1),
    yFrac: Math.min(Math.max((box.y + box.h / 2) / entry.h, 0), 1),
    box: { x: box.x, y: box.y, w: box.w, h: box.h }
  };
}

// Enumerate EVERY balloon callout on an image entry (for the reverse direction:
// click a balloon -> go to its part). Reconstructs two-line balloons ("70" over
// "AP" => "70AP") + keeps standalone codes ("2S","22E","1RCV"). Filters to
// item-like tokens (contain a digit, 2-6 chars) so printed section labels
// ("AUGER","SPIRAL GROOVE LINERS") aren't treated as clickable. Returns
// [{ code, box }] in native-pixel coords; dedup'd by code + rough position.
export function extractBalloons(entry) {
  var toks = (entry && entry.t) || [];
  var out = [], seen = {};
  function push(code, box) {
    var c = normalize(code);
    if (!c || c.length < 2 || c.length > 6 || !/[0-9]/.test(c)) return;
    var key = c + "@" + Math.round(box.x / 8) + "," + Math.round(box.y / 8);
    if (seen[key]) return;
    seen[key] = 1;
    out.push({ code: c, box: { x: box.x, y: box.y, w: box.w, h: box.h } });
  }
  for (var i = 0; i < toks.length; i++) {
    var t = toks[i], s = normalize(t.s);
    if (!s) continue;
    if (/^[0-9]+$/.test(s)) {
      // pure number: try to merge with an alpha-bearing token stacked below it
      var merged = false;
      for (var j = 0; j < toks.length; j++) {
        if (j === i) continue;
        var u = toks[j], su = normalize(u.s);
        if (!su || /^[0-9]+$/.test(su)) continue;
        var dx = Math.abs((t.x + t.w / 2) - (u.x + u.w / 2));
        var gap = u.y - (t.y + t.h);
        if (dx <= Math.max(t.w, u.w) && gap >= -t.h && gap <= t.h * 1.8) {
          var x = Math.min(t.x, u.x), y = Math.min(t.y, u.y);
          push(s + su, { x: x, y: y, w: Math.max(t.x + t.w, u.x + u.w) - x, h: Math.max(t.y + t.h, u.y + u.h) - y });
          merged = true;
        }
      }
      if (!merged) push(s, t); // bare number balloon (e.g. "41")
    } else if (/[0-9]/.test(s)) {
      push(s, t); // already a complete code: "2S", "22E", "1RCV", "8ZG"
    }
  }
  return out;
}
